/* eslint-disable camelcase */
import { ReactNode, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { PeopleAltOutlined } from '@mui/icons-material';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';
import { InvoiceMissionOptions } from 'src/shared/models/options/invoiceMissionOptions';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { getInvoiceTemplateById } from 'src/shared/services/invoiceService';
import { InvoiceTemplate, ProfileItemResponse } from 'src/modules/invoice/models/invoiceTemplate';
import { ProfileFilter, useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';
import { SendingPreviousMissionPicker } from 'src/modules/sending/components/sendingPreviousMissionPicker';

const toFilter = (p: ProfileItemResponse): ProfileFilter => ({
  groupId: p.memberProfile_id,
  groupTitle: p.groupName,
  itemId: p.memberProfileItem_id,
  itemName: p.name,
});

const profileChips = (items: ProfileItemResponse[], emptyText: string): ReactNode =>
  items.length > 0 ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {items.map((p) => (
        <Chip key={p.memberProfileItem_id} label={`${p.groupName}: ${p.name}`} size='small' variant='outlined' />
      ))}
    </Box>
  ) : (
    <Typography variant='body2' color='text.secondary'>
      {emptyText}
    </Typography>
  );

interface SendingCopySelectionModalProps {
  open: boolean;
  onClose: () => void;
}

// "Gör som en tidigare avisering": välj en tidigare medlemsavisering och kopiera HELA profilurvalet (både de profiler
// som ska inkluderas och de som ska undantas). Medlemsåret styrs fortfarande av det här utskickets datum + friperiod.
export const SendingCopySelectionModal = ({ open, onClose }: SendingCopySelectionModalProps) => {
  const isMounted = useIsMounted();
  const {
    setProfileFilters,
    setExcludeProfileFilters,
    setSelectionTab,
    setDueConfirmed,
    setIncludeConfirmed,
    setExcludeConfirmed,
    setCopyFromMissionId,
  } = useSendingWizard();

  const [missions, setMissions] = useState<InvoiceMissionResponse[]>([]);
  const [selected, setSelected] = useState<InvoiceMissionResponse | null>(null);
  const [template, setTemplate] = useState<InvoiceTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setListLoading(true);
      try {
        const opts = new InvoiceMissionOptions();
        opts.pageIndex = 0;
        // Hämtar bara de 10 senaste (sorterat fallande) för att hålla laddningstiden nere hos kunder med många aviseringar.
        opts.pageSize = 10;
        const res = await invoiceMissionService.getAll(opts);
        if (isMounted()) setMissions((res.data.results ?? []).filter((m) => m.type === InvoiceMissionTypeTextEnum.member));
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setListLoading(false);
      }
    })();
  }, [open, isMounted]);

  const handleClose = () => {
    setSelected(null);
    setTemplate(null);
    onClose();
  };

  const onSelect = async (m: InvoiceMissionResponse | null) => {
    setSelected(m);
    setTemplate(null);
    if (!m || !m.template_id) return;
    setLoading(true);
    try {
      const res = await getInvoiceTemplateById(m.template_id);
      if (isMounted()) setTemplate(res.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const includeItems = template?.profilesIncludeItems ?? [];
  const excludeItems = template?.profilesExcludeItems ?? [];

  // Kopiera hela profilurvalet till detta utskick och markera profil-stegen som klara. Läge = eget urval.
  const apply = () => {
    setProfileFilters(includeItems.map(toFilter));
    setExcludeProfileFilters(excludeItems.map(toFilter));
    // Läge 2 = "kopierat från tidigare avisering" (så knappen förblir markerad, inte "Välj vilka medlemmar").
    setSelectionTab(2);
    // Kom ihåg vilken avisering urvalet kopierades från, så Texter-steget kan förvälja samma vid "Hämta texter".
    setCopyFromMissionId(selected?.invoiceMission_id ?? null);
    // Markera alla urvals-steg som klara så resultatet visas direkt (medlemsåret är redan härlett ur datumen).
    setDueConfirmed(true);
    setIncludeConfirmed(true);
    setExcludeConfirmed(true);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Gör som en tidigare avisering</DialogTitle>
      <DialogContent dividers>
        <SendingPreviousMissionPicker
          missions={missions}
          selectedId={selected?.invoiceMission_id ?? null}
          onSelect={onSelect}
          emptyText='Inga tidigare medlemsaviseringar'
          limitNote='Visar de 10 senaste aviseringarna.'
          loading={listLoading}
        />

        {loading && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!template && !loading && (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <PeopleAltOutlined sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant='body2'>Välj en avisering ovan för att se dess urval.</Typography>
          </Box>
        )}

        {template && !loading && (
          <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
                Ta bara med dem som även har profilerna:
              </Typography>
              {profileChips(includeItems, 'Inget profilvillkor')}
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
                Undanta dem som har profilerna:
              </Typography>
              {profileChips(excludeItems, 'Inga undantag')}
            </Box>
            <Typography variant='caption' color='text.secondary'>
              Medlemsåret styrs av det här utskickets utskicksdatum och sista betaldatum – inte av den tidigare aviseringen.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color='inherit' onClick={handleClose}>
          Avbryt
        </Button>
        <Button variant='contained' disableElevation onClick={apply} disabled={!template}>
          Kopiera urvalet
        </Button>
      </DialogActions>
    </Dialog>
  );
};
